import { TipoActividad } from "@/shared/types/enums/TipoActividadNotificaciones.enum";
import { Switch } from "@mui/material";
import { RiCalendarCheckLine, RiCalendarCloseLine, RiCalendarEventLine, RiChat1Line, RiFileAddLine, RiGovernmentLine, RiLockPasswordLine, RiNotificationLine } from "@remixicon/react";
import { useMemo, type FunctionComponent } from "react";

const tailwindColorMap: { [key: string]: string } = {
    'bg-sky-500': '#0ea5e9',    // sky-500
    'bg-teal-500': '#14b8a6',   // teal-500
    'bg-emerald-500': '#10b981',// emerald-500
    'bg-green-500': '#22c55e',  // green-500
    'bg-red-500': '#ef4444',    // red-500
    'bg-yellow-500': '#f59e0b', // yellow-500
    '#0050DA': '#0050DA',
};

export const getHexColorFromTailwind = (tailwindClass: string): string | undefined => {
    return tailwindColorMap[tailwindClass];
};

export enum TipoActividadConfigNotificaciones{
    DocumentoAgregado = 1,
    FijacionCreada = 2,
    AudienciaCelebrada = 3,
    SalaAsignada = 4,
    SalaRevocada = 5,
    MensajeTribunal = 6,
    AudienciaCancelada = 7,
}

const ICON_BY_NOFICATION_TYPE : Record<TipoActividad, React.ReactNode> = {
    [TipoActividad.DocumentoAgregado]: <RiFileAddLine color="#0ea5e9" size={32} />,
    [TipoActividad.FijacionCreada]: <RiCalendarEventLine color="#14b8a6" size={32} />,
    [TipoActividad.AudienciaCelebrada]: <RiCalendarCheckLine color="#10b981" size={32} />,
    [TipoActividad.SalaAsignada]: <RiGovernmentLine color="#22c55e" size={32} />,
    [TipoActividad.SalaRevocada]: <RiGovernmentLine color="#ef4444" size={32} />,
    [TipoActividad.MensajeTribunal]: <RiChat1Line color="#f59e0b" size={32} />,
    [TipoActividad.AudienciaCancelada]: <RiCalendarCloseLine color="#ef4444" size={32} />,
    [TipoActividad.TwoFactorAuthenticator]: <RiLockPasswordLine color="#0ea5e9" size={32} />,
}

interface ItemConfigProps {
    TipoActividad: TipoActividad
    title: string
    desc: string
    hardColor: string
    lightColor: string
    habilitado: boolean
    onChange?: (value: boolean) => void;
    width?: string
}

const ItemConfig: FunctionComponent<ItemConfigProps> = ({
    TipoActividad, title, desc, hardColor, lightColor, habilitado, onChange, width
}) => {    
    const switchSx = useMemo(() => {
        const switchColor = getHexColorFromTailwind(hardColor);
        const activeColor = switchColor || '#0050DA'; // Valor por defecto si falla el mapeo
        
        return {
            '& .MuiSwitch-switchBase.Mui-checked': {
                color: `${activeColor} !important`,

                '& + .MuiSwitch-track': {
                    backgroundColor: `${activeColor} !important`, 
                    opacity: '0.5', 
                },
                
                '&:hover': {
                    backgroundColor: `${activeColor}1A`, // 1A es opacidad 10% en hex
                },
            },
            
            // Configuración de estado inactivo (apagado)
            '& .MuiSwitch-track': {
                backgroundColor: '#9ca3af',
                opacity: '1',
            },
            
            '& .MuiSwitch-switchBase:not(.Mui-checked)': {
                // Esto cambia el color del pulgar cuando está inactivo
                color: '#e0e0e0',
            },
        };
    }, [hardColor]);

    return (
        <>
        <div className={`flex gap-2 items-center border-1 border-gray-200 rounded-lg px-4 py-2 justify-between ${lightColor} ${width ? width : 'max-w-[410px]'}`}>
            <div className="flex  gap-2">
                <div className="pr-2">
                    <div className="p-2 bg-white border-1 border-gray-300 rounded-full">
                        <span>{ICON_BY_NOFICATION_TYPE[TipoActividad] || <RiNotificationLine color="#4F99EF" size={32} />}</span>
                    </div>
                </div>
                <div>
                    <h4 className="h4 font-Bold">
                        {title}
                    </h4>
                    <p className="text-mainGray">
                        {desc}
                    </p>
                </div>
            </div>
            <Switch 
                checked={habilitado}
                sx={switchSx}
                onChange={(e) => onChange?.(e.target.checked)}
            />
        </div>
        </>
    );
}

export default ItemConfig;